import React, { useEffect, useState } from 'react';
import OrganizationsList from './OrganizationsList';
import { useNavigate } from 'react-router-dom';
import { Organization } from 'components/common/ObjectTypes';
import { ManageOrganizationModal } from './ManageOrganizationModal';
import { useMyDetails } from 'api/users/useMyDetails';
import { useListQuery } from 'api/useListQuery';
import { queryKeys } from 'api/queryKeys';
import { loadOrganizations } from 'api/organizations/loadOrganizations';
import { page_size } from 'api/apiUtils';
import { useHelp } from 'context/HelpContext';
import { useTranslation } from 'react-i18next';

const OrganizationPage = () => {
  const { t, i18n } = useTranslation();
  const { setHelpContent } = useHelp();

  useEffect(() => {
    setHelpContent({
      cards: [
        {
          title: t('Managing Workspaces in Gundi'),
          description: t(
            'Learn how to create and manage workspaces and their members.',
          ),
          button: {
            label: t('Learn More'),
            url: 'https://support.earthranger.com/gundi-interface/workspaces',
          },
        },
      ],
    });
  }, [i18n.language, t, setHelpContent]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);
  const [searchString, setSearchString] = useState<string>(() => {
    const orgSearchString = sessionStorage.getItem('orgSearchString');
    return orgSearchString !== null ? orgSearchString : '';
  });
  const [addOrgModal, setAddOrgModal] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const navigate = useNavigate();
  const workspaces = useListQuery<Organization>(
    queryKeys.organizations.page({ search: searchString, offset }),
    async (token) => {
      const [orgs, total_orgs, error] = await loadOrganizations(
        token,
        offset,
        searchString,
        [],
        true,
      );
      if (error) throw error;
      return { results: orgs, count: total_orgs };
    },
  );
  const myDetails = useMyDetails();

  useEffect(() => {
    sessionStorage.setItem('orgSearchString', searchString);
  }, [searchString]);

  // GlobalSearch owns the typed value and hands over the term on submit or
  // clear, so this is the only place the applied search is set.
  const handleSearch = (term: string) => {
    setSearchString(term);
    setOffset(0);
  };

  const handleSelectOrg = (org_id: string) => {
    navigate('/workspaces/' + org_id);
  };

  const handleToggleOrgModal = (saved?: boolean) => {
    // A save refreshes every workspace list itself (see organizationsApi).
    setAddOrgModal(!addOrgModal);
  };

  const handlePageClick = (e: any) => {
    setOffset(e.selected * page_size);
  };

  return (
    <div>
      {addOrgModal && (
        <ManageOrganizationModal
          onToggleModal={handleToggleOrgModal}
          newOrg={true}
        />
      )}
      <OrganizationsList
        data-testid="organizations_list"
        organizations={workspaces.results}
        onSelectOrg={handleSelectOrg}
        onAddOrg={() => handleToggleOrgModal()}
        superuser={!!myDetails?.is_superuser}
        onPageClicked={handlePageClick}
        totalCount={workspaces.count}
        pageLoaded={workspaces.settled}
        take={page_size}
        offset={offset}
        searchString={searchString}
        onSearch={handleSearch}
      />
    </div>
  );
};

export default OrganizationPage;
